import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
  highlight?: boolean;
}

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 10);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems: NavItem[] = [
    { label: 'Home', path: '/' },
    {
      label: 'Training',
      path: '/training',
      children: [
        { label: 'Tailwheel Endorsement', path: '/training/tailwheel-endorsement' },
        { label: 'CFI Spin Training', path: '/training/cfi-spin-training' },
        { label: 'Tailwheel Refresh', path: '/training/tailwheel-refresh' },
        { label: 'Flight Review', path: '/training/flight-review' },
      ],
    },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Blog', path: '/blog' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Resources', path: '/resources' },
    { label: 'About', path: '/about' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Inquire Now', path: '/inquire', highlight: true },
  ];

  const handleNavClick = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDropdownNavClick = () => {
    setOpenDropdown(null);
    handleNavClick();
  };

  const isActive = (item: NavItem) => {
    if (item.children) {
      return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    }
    return location.pathname === item.path;
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/90 backdrop-blur-sm py-4'
    }`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/" className="text-xl font-medium text-primary">
            Total Tailwheel
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex space-x-1" ref={dropdownRef}>
          {navItems.map((item) =>
            item.children ? (
              <div key={item.path} className="relative">
                <button
                  onClick={() => setOpenDropdown(openDropdown === item.path ? null : item.path)}
                  className={`flex items-center gap-1 px-3 py-2 text-sm rounded transition-colors ${
                    isActive(item)
                      ? 'text-primary bg-primary/10'
                      : 'text-foreground hover:text-primary hover:bg-primary/5'
                  }`}
                >
                  {item.label}
                  <ChevronDown size={14} className={`transition-transform ${openDropdown === item.path ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === item.path && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px]">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={handleDropdownNavClick}
                        className="block px-4 py-2 text-sm text-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={
                  item.highlight
                    ? 'px-4 py-2 text-sm font-semibold rounded bg-primary text-white hover:bg-primary-dark transition-colors'
                    : `px-3 py-2 text-sm rounded transition-colors ${
                        isActive(item)
                          ? 'text-primary bg-primary/10'
                          : 'text-foreground hover:text-primary hover:bg-primary/5'
                      }`
                }
              >
                {item.label}
              </Link>
            )
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-primary p-4 min-w-[56px] min-h-[56px] flex items-center justify-center text-2xl"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 py-4">
          <div className="container mx-auto px-4 flex flex-col space-y-1">
            {navItems.map((item) => (
              <div key={item.path}>
                {item.children ? (
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.path ? null : item.path)}
                    className={`flex items-center justify-between w-full text-left py-2 px-4 rounded transition-colors ${
                      isActive(item)
                        ? 'text-primary bg-primary/10'
                        : 'hover:bg-secondary/30'
                    }`}
                  >
                    {item.label}
                    <ChevronDown size={16} className={`transition-transform ${mobileExpanded === item.path ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleNavClick();
                    }}
                    className={
                      item.highlight
                        ? 'block text-left py-2 px-4 rounded font-semibold bg-primary text-white hover:bg-primary-dark transition-colors'
                        : `block text-left py-2 px-4 rounded transition-colors ${
                            isActive(item)
                              ? 'text-primary bg-primary/10'
                              : 'hover:bg-secondary/30'
                          }`
                    }
                  >
                    {item.label}
                  </Link>
                )}
                {item.children && mobileExpanded === item.path && (
                  <div className="ml-4 border-l-2 border-primary/20 pl-2 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          handleDropdownNavClick();
                        }}
                        className="block py-2 px-4 text-sm text-foreground/80 hover:text-primary hover:bg-primary/5 rounded transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
